/**
 * Achievements. Display-only apart from the small per-achievement EPS bonus
 * (see ACHIEVEMENT_EPS_BONUS in economy.ts). The "hatch each tier" set is
 * templated from DINOS. Ids are saved, so they keep their original spelling
 * (first:, herd:) even though the wording has moved on.
 */

import { DINOS, RESIDENT_IDS, aOrAn } from './dinos';
import { ERA_MODIFIERS, EXPEDITIONS_BY_ID } from './eras';
import { formatEggs, groupDigits } from '../game/numbers';
import type { Condition } from '../game/conditions';

export interface AchievementDef {
  id: string;
  name: string;
  description: string;
  condition: Condition;
}

const eggMilestones: AchievementDef[] = [
  [100, 'First Clutch'],
  [10_000, 'Egg Basket'],
  [1_000_000, 'Egg Mountain'],
  [1_000_000_000, 'Egg Empire'],
  [1_000_000_000_000, 'Eggs Beyond Counting'],
].map(([amount, name]) => ({
  id: `eggs:${amount}`,
  name: String(name),
  description: `Collect ${groupDigits(Number(amount))} eggs in total.`,
  condition: { kind: 'totalEggs', amount: Number(amount) },
}));

const clickMilestones: AchievementDef[] = [
  [100, 'Warming Up'],
  [1_000, 'Sore Finger'],
  [10_000, 'Egg Whisperer'],
  [100_000, 'Tireless'],
].map(([count, name]) => ({
  id: `clicks:${count}`,
  name: String(name),
  description: `Click the egg ${groupDigits(Number(count))} times.`,
  condition: { kind: 'clicks', count: Number(count) },
}));

/** Level milestones for every tier, with a title per rung. */
export const LEVEL_MILESTONES: ReadonlyArray<readonly [level: number, title: string]> = [
  [10, 'Juvenile'],
  [25, 'Grown'],
  [50, 'Mighty'],
  [100, 'Legendary'],
  [250, 'Mythical'],
];

/** Per tier, in order: hatch it, then each level milestone. */
const perDinosaur: AchievementDef[] = DINOS.flatMap((d) => [
  {
    id: `first:${d.id}`,
    name: `Meet the ${d.name}`,
    description: `Hatch ${aOrAn(d.name)} ${d.name}.`,
    condition: { kind: 'dinoLevel', dinoId: d.id, level: 1 },
  },
  ...LEVEL_MILESTONES.map(([level, title]) => ({
    id: `level:${d.id}:${level}`,
    name: `${title} ${d.name}`,
    description: `Raise your ${d.name} to level ${level}.`,
    condition: { kind: 'dinoLevel' as const, dinoId: d.id, level },
  })),
]);

const HOUR = 60 * 60;
const DAY = 24 * HOUR;

/** The first dinosaur only a Prime Splice evolves. */
const FIRST_PRIME = DINOS.find((d) => d.splice === 'prime')?.name ?? 'dinosaur';

/** The whole herd at once, and the two flyers. */
const herd: AchievementDef[] = [
  {
    id: 'herd:full-house',
    name: 'Full House',
    description: 'Have every available dinosaur hatched at once.',
    condition: { kind: 'herdAtLevel', level: 1 },
  },
  {
    id: 'herd:even-keel',
    name: 'Even Keel',
    description: 'Have every available dinosaur at level 25 or higher at once.',
    condition: { kind: 'herdAtLevel', level: 25 },
  },
  {
    id: 'herd:legends',
    name: 'Hall of Legends',
    description: 'Have every available dinosaur at level 100 or higher at once.',
    condition: { kind: 'herdAtLevel', level: 100 },
  },
  {
    id: 'herd:air-superiority',
    name: 'Air Superiority',
    description: 'Raise both flyers, the Pteranodon and the Quetzalcoatlus, to level 50.',
    condition: { kind: 'dinosAtLevel', dinoIds: ['pteranodon', 'quetzalcoatlus'], level: 50 },
  },
  {
    id: 'herd:express',
    name: 'Express Delivery',
    description: 'Hatch a Baryonyx within 12 hours of an era starting.',
    condition: { kind: 'fastHatch', dinoId: 'baryonyx', seconds: 12 * HOUR },
  },
];

const epsMilestones: AchievementDef[] = [
  [1, 'Ember'],
  [10, 'Lava Drip'],
  [100, 'Lava Stream'],
  [1_000, 'Lava River'],
  [10_000, 'Lava Flood'],
  [100_000, 'Lava Wave'],
  [1_000_000, 'Eruption'],
  [10_000_000, 'Lava Planet'],
].map(([amount, name]) => ({
  id: `eps:${amount}`,
  name: String(name),
  description: `Reach ${groupDigits(Number(amount))} ${Number(amount) === 1 ? 'egg' : 'eggs'} per second.`,
  condition: { kind: 'eps', amount: Number(amount) },
}));

const fallingEggs: AchievementDef[] = [
  {
    id: 'cracks:1',
    name: 'Lucky Catch',
    description: 'Catch a falling egg.',
    condition: { kind: 'cracks', count: 1 },
  },
  {
    id: 'cracks:50',
    name: 'Egg Hunter',
    description: 'Catch 50 falling eggs.',
    condition: { kind: 'cracks', count: 50 },
  },
  {
    id: 'cracks:250',
    name: 'Golden Hoard',
    description: 'Catch 250 falling eggs.',
    condition: { kind: 'cracks', count: 250 },
  },
  {
    id: 'cracks:777',
    name: 'Lucky Sevens',
    description: 'Catch 777 falling eggs.',
    condition: { kind: 'cracks', count: 777 },
  },
  {
    id: 'cracks:7777',
    name: 'Seventh Heaven',
    description: 'Catch 7,777 falling eggs.',
    condition: { kind: 'cracks', count: 7777 },
  },
  {
    id: 'golden:double-up',
    name: 'Double Up',
    description: 'Have a production boost and a click boost running at the same time.',
    condition: { kind: 'doubleBoost' },
  },
  {
    id: 'golden:thunderclap',
    name: 'Thunderstruck',
    description: 'Catch a golden egg that gives Thunderclap.',
    condition: { kind: 'bonusCaught', bonusId: 'thunderclap' },
  },
  {
    id: 'golden:motherlode',
    name: 'Motherlode',
    description: 'Catch a golden egg that gives Egg Motherlode.',
    condition: { kind: 'bonusCaught', bonusId: 'motherlode' },
  },
];

const naming: AchievementDef[] = [
  {
    id: 'names:1',
    name: 'Name Tag',
    description: 'Give a dinosaur a name.',
    condition: { kind: 'named', count: 1 },
  },
  {
    id: 'names:all',
    name: 'Roll Call',
    description: 'Give every dinosaur a name, across all your eras.',
    condition: { kind: 'named', count: DINOS.length, ever: true },
  },
  {
    id: 'names:namesake',
    name: 'Very Original',
    description: 'Name a dinosaur after its own species.',
    condition: { kind: 'namesake' },
  },
];

const sharing: AchievementDef[] = [
  {
    id: 'share:1',
    name: 'Word of Mouth',
    description: 'Share DinoClicker with someone.',
    condition: { kind: 'shared' },
  },
  {
    id: 'rules:1',
    name: 'By the Book',
    description: 'Read the rules.',
    condition: { kind: 'feat', feat: 'rules' },
  },
];

const meteors: AchievementDef[] = [
  {
    id: 'meteors:1',
    name: 'Rockbreaker',
    description: 'Break a meteor.',
    condition: { kind: 'meteorsBroken', count: 1 },
  },
  {
    id: 'meteors:25',
    name: 'Quarry',
    description: 'Break 25 meteors.',
    condition: { kind: 'meteorsBroken', count: 25 },
  },
  {
    id: 'meteors:core',
    name: 'Core Sample',
    description: 'Break a Core meteor.',
    condition: { kind: 'meteorKindBroken', kindId: 'core', count: 1 },
  },
  {
    // The hardest rock in the sky: 420 health in ten seconds needs the drill
    // and a fast hand, so this is the one meteor achievement that is earned
    // rather than arrived at.
    id: 'meteors:nova',
    name: 'Star Cracker',
    description: 'Break a Nova meteor.',
    condition: { kind: 'meteorKindBroken', kindId: 'nova', count: 1 },
  },
  {
    id: 'shards:100',
    name: 'Shard Collector',
    description: 'Earn 100 meteor shards.',
    condition: { kind: 'shardsEarned', amount: 100 },
  },
  {
    id: 'meteors:geologist',
    name: 'Geologist',
    description: 'Break one meteor of each of the five kinds.',
    condition: { kind: 'allMeteorKinds' },
  },
  {
    id: 'meteors:shower',
    name: 'Shower Stopper',
    description: 'Break every rock in a meteor shower before any of them cools.',
    condition: { kind: 'feat', feat: 'shower' },
  },
  {
    id: 'meteors:last-second',
    name: 'Last Second',
    description: 'Break a meteor with under a second left on its countdown.',
    condition: { kind: 'feat', feat: 'last-second' },
  },
  {
    id: 'shards:hoard',
    name: 'Shard Hoard',
    description: 'Hold 500 meteor shards at once.',
    condition: { kind: 'shardsHeld', amount: 500 },
  },
  {
    id: 'meteors:diamond-drill',
    name: 'Fully Equipped',
    description: 'Own the Diamond Drill.',
    condition: { kind: 'ownsShopItem', itemId: 'tool:diamond-drill' },
  },
];

const eras: AchievementDef[] = [
  {
    id: 'eras:1',
    name: 'The First Extinction',
    description: 'Trigger your first extinction.',
    condition: { kind: 'eras', count: 1 },
  },
  {
    id: 'eras:5',
    name: 'Five Eras',
    description: 'End five eras.',
    condition: { kind: 'eras', count: 5 },
  },
  {
    id: 'eras:10',
    name: 'Ten Eras',
    description: 'End ten eras.',
    condition: { kind: 'eras', count: 10 },
  },
  {
    id: 'eras:fossils',
    name: 'Palaeontologist',
    description: 'Earn 100 fossils.',
    condition: { kind: 'fossilsEarned', count: 100 },
  },
  {
    id: 'eras:preserved',
    name: 'Preserved',
    description: 'Excavate the same dinosaur three eras running.',
    condition: { kind: 'excavatedStreak', count: 3 },
  },
  {
    id: 'eras:buried',
    name: 'Let Them Rest',
    description: 'End an era without excavating a fossil.',
    condition: { kind: 'leftBuried' },
  },
  {
    id: 'eras:survivor',
    name: 'Survivor',
    description: 'See an expedition through to its end.',
    condition: { kind: 'expeditionDone' },
  },
  {
    id: 'eras:deep-time',
    name: 'Deep Time',
    description: 'Lay a hundred trillion eggs in a single era.',
    condition: { kind: 'eraEggs', amount: 1e14 },
  },
  {
    id: 'eras:well-travelled',
    name: 'Well Travelled',
    description: `Play an era of every kind: ${ERA_MODIFIERS.slice(0, -1)
      .map((m) => m.name)
      .join(', ')} and ${ERA_MODIFIERS[ERA_MODIFIERS.length - 1]?.name}.`,
    condition: { kind: 'allEraModifiers' },
  },
  {
    id: 'eras:barren',
    name: 'Barren Ground',
    description: 'Finish a Barren expedition.',
    condition: { kind: 'expeditionFinished', tripId: 'trip:barren' },
  },
  {
    id: 'eras:hands-off',
    name: 'Hands in Pockets',
    description: 'Finish a Hands Off expedition.',
    condition: { kind: 'expeditionFinished', tripId: 'trip:hands-off' },
  },
  {
    id: 'eras:photo-finish',
    name: 'Photo Finish',
    description: `Lay ${formatEggs(EXPEDITIONS_BY_ID.get('trip:sprint')?.requiresEggs ?? 1e12)} eggs in a Sprint before it ends.`,
    condition: { kind: 'sprintWon' },
  },
  {
    id: 'eras:quick',
    name: 'Quick Extinction',
    description: 'End an era within 5 hours of it starting.',
    condition: { kind: 'quickEra', seconds: 5 * HOUR },
  },
  {
    id: 'eras:long-reign',
    name: 'Long Reign',
    description: 'Let an era run for 7 days before ending it.',
    condition: { kind: 'longEra', seconds: 7 * DAY },
  },
  {
    id: 'eras:rich-seam',
    name: 'Rich Seam',
    description: 'Earn 10 fossils from a single extinction.',
    condition: { kind: 'eraFossils', count: 10 },
  },
  {
    id: 'eras:name-in-bronze',
    name: 'Name in Bronze',
    description: 'Excavate a dinosaur you had named.',
    condition: { kind: 'excavatedNamed' },
  },
];

const museum: AchievementDef[] = [
  {
    id: 'museum:opening-day',
    name: 'Opening Day',
    description: "Put five dinosaurs on show in the Museum's Collection.",
    condition: { kind: 'onShow', count: 5 },
  },
  {
    id: 'museum:patron',
    name: 'Patron',
    description: 'Buy every Museum perk.',
    condition: { kind: 'allPerks' },
  },
];

const away: AchievementDef[] = [
  {
    id: 'away:sleepwalker',
    name: 'Sleepwalker',
    description: 'Reach an away rate of 100%.',
    condition: { kind: 'awayRate', rate: 1 },
  },
  {
    id: 'away:long-nap',
    name: 'Long Nap',
    description: 'Come back after the full away limit and collect every hour of it.',
    condition: { kind: 'feat', feat: 'long-nap' },
  },
];

const evolution: AchievementDef[] = [
  {
    id: 'evolved:1',
    name: 'Next of Kin',
    description: 'Evolve a dinosaur.',
    condition: { kind: 'evolved', count: 1 },
  },
  {
    id: 'evolved:5',
    name: 'Branching Out',
    description: 'Evolve five dinosaurs.',
    condition: { kind: 'evolved', count: 5 },
  },
  {
    id: 'evolved:all',
    name: 'Whole New World',
    description: 'Evolve every dinosaur, across all your eras.',
    condition: { kind: 'evolved', count: DINOS.length, ever: true },
  },
  {
    id: 'evolved:named',
    name: 'Still You',
    description: 'Evolve a dinosaur you had named.',
    condition: { kind: 'evolvedNamed' },
  },
  {
    id: 'evolved:prime',
    name: 'Prime Time',
    description: `Evolve ${aOrAn(FIRST_PRIME)} ${FIRST_PRIME} or anything bigger, which takes a Prime Splice.`,
    condition: { kind: 'evolvedNeeding', splice: 'prime' },
  },
];

/**
 * Every era's resident, hatched across your eras (docs/era-residents-plan.md).
 * Only while residents are in the roster: with none, it would be met at once.
 */
const residents: AchievementDef[] =
  RESIDENT_IDS.length > 0
    ? [
        {
          id: 'residents:all',
          name: 'Grand Tour',
          description: "Hatch every era's resident dinosaur, across all your eras.",
          condition: { kind: 'hatchedEver', dinoIds: RESIDENT_IDS },
        },
      ]
    : [];

export const ACHIEVEMENTS: readonly AchievementDef[] = [
  ...eggMilestones,
  ...clickMilestones,
  ...perDinosaur,
  ...herd,
  ...epsMilestones,
  ...fallingEggs,
  ...naming,
  ...sharing,
  ...meteors,
  ...evolution,
  ...eras,
  ...residents,
  ...museum,
  ...away,
];

export const ACHIEVEMENTS_BY_ID: ReadonlyMap<string, AchievementDef> = new Map(
  ACHIEVEMENTS.map((a) => [a.id, a]),
);
